import type { PublicFont } from '@repo/api-contracts';
import { registerFontFace } from '@repo/design-viewer';
import { useEffect, useRef, useState } from 'react';

export type FontLoadStatus = 'loading' | 'loaded' | 'failed';

interface FontFacesOptions {
  readonly loadFont?: (font: PublicFont) => Promise<void>;
}

export interface FontFaces {
  readonly statusOf: (fontId: string) => FontLoadStatus | undefined;
  /** 로드 중이거나 끝난 폰트의 패밀리 이름. 실패했거나 아직 요청하지 않았으면 undefined — 호출자가 대체 글꼴을 쓴다. */
  readonly familyOf: (fontId: string) => string | undefined;
  /** 주어진 폰트가 모두 끝났는지(loaded·failed). 허용 목록에 없는 id 는 렌더러가 대체 글꼴로 그리므로 끝난 것으로 본다. */
  readonly settledFor: (fontIds: readonly string[]) => boolean;
  /** 필요한 폰트(neededFontIds) 전체가 끝났는지. 필요한 폰트가 없으면 참이다. */
  readonly allSettled: boolean;
}

/**
 * 서명 URL 의 폰트 파일을 FontFace 로 등록한다. 패밀리 이름은 계약 familyName 그대로라 렌더러 fontString 과 맞는다.
 * 등록 실패는 던져서 'failed' 로 남긴다(호출자가 대체 글꼴을 쓴다).
 */
const loadPublicFont = async (font: PublicFont): Promise<void> => {
  const outcome = await registerFontFace({ family: font.familyName, url: font.fileUrl });
  if (outcome === 'failed') throw new Error(`폰트 ${font.familyName} 을 불러오지 못했습니다.`);
};

const isSettled = (status: FontLoadStatus | undefined): boolean =>
  status === 'loaded' || status === 'failed';

/**
 * 허용 폰트 가운데 문서가 쓰는 것(neededFontIds)만 지연 로드하고 상태를 돌려준다. 나중에 필요해진 폰트는 그때 로드하고,
 * 같은 id·URL 은 한 번만 로드하며 URL 이 바뀌면(만료 뒤 재조회) 다시 로드한다.
 * 실패한 폰트는 'failed' — familyOf 가 undefined 를 돌려 호출자가 대체 글꼴로 그린다.
 */
export const useFontFaces = (
  fonts: readonly PublicFont[],
  neededFontIds: readonly string[],
  options: FontFacesOptions = {},
): FontFaces => {
  const loadFont = options.loadFont ?? loadPublicFont;
  const [status, setStatus] = useState<ReadonlyMap<string, FontLoadStatus>>(new Map());
  const attempted = useRef(new Map<string, string>());
  const neededKey = [...new Set(neededFontIds)].sort().join(',');

  // biome-ignore lint/correctness/useExhaustiveDependencies: neededFontIds 는 내용 키(neededKey)로만 비교한다.
  useEffect(() => {
    const needed = new Set(neededFontIds);
    for (const font of fonts) {
      if (!needed.has(font.id) || attempted.current.get(font.id) === font.fileUrl) continue;
      attempted.current.set(font.id, font.fileUrl);
      setStatus((prev) => new Map(prev).set(font.id, 'loading'));
      void loadFont(font)
        .then(() => setStatus((prev) => new Map(prev).set(font.id, 'loaded')))
        .catch(() => setStatus((prev) => new Map(prev).set(font.id, 'failed')));
    }
  }, [fonts, neededKey, loadFont]);

  const families = new Map(fonts.map((font) => [font.id, font.familyName] as const));
  const settledFor = (fontIds: readonly string[]): boolean =>
    fontIds.every((id) => !families.has(id) || isSettled(status.get(id)));
  return {
    statusOf: (fontId) => status.get(fontId),
    familyOf: (fontId) => {
      const current = status.get(fontId);
      return current === 'loaded' || current === 'loading' ? families.get(fontId) : undefined;
    },
    settledFor,
    allSettled: settledFor(neededFontIds),
  };
};
